import { Globe } from 'lucide-react';
import { StepCard } from '../components/StepCard';
import { useStartingUniverse } from './hooks';
import { Universe } from './sections/Universe/Universe';
import { EligibilityRules } from './sections/Rules/EligibilityRules';
import { BenchmarkSelection } from './sections/Benchmark/BenchmarkSelection';
import { useFilteredSecurities } from '../../hooks/useFilteredSecurities';

// Investment Universe & Benchmark — Step 1 of the strategy wizard.
//
// The page renders the real form as soon as the universe API call
// resolves. The parent /strategies route now warms the cache the
// moment the user lands on the dashboard (see
// strategies.routes.tsx → strategiesRoute.loader), so by the time
// the user finishes the 3-step NewStrategyWizard modal and enters
// this step, `selectedUniverse` is usually already populated and the
// skeleton never paints. On a true cold cache (e.g. someone deep-links
// directly into the form) the skeleton holds for the few hundred ms
// the call takes — same behaviour as before, just with the cache
// warm-up moved upstream so the common case is instant.
export function StartingUniverse() {
  const { pointInTime, eligibility, benchmark } = useStartingUniverse();
  // Live per-selector "N excluded" estimates for the Geographic Scope / Size
  // Segment / Cyclicality headers — same badge as the eligibility filters.
  const { filterExcludedCounts } = useFilteredSecurities();

  return (
    <StepCard
      Icon={Globe}
      title="Investment Universe & Benchmark"
      description="Select the investment universe for portfolio construction"
      bodySpacing="space-y-4"
    >
      {/* The Universe section internally skeletons only its catalog-bound
          widgets (selector / stats / details); the header + multi-select groups
          render regardless of ClickHouse availability. */}
      <Universe
        universeId={pointInTime.universeId}
        geographicScope={pointInTime.geographicScope}
        sizeSegment={pointInTime.sizeSegment}
        cyclicality={pointInTime.cyclicality}
        selectedUniverse={pointInTime.selectedUniverse}
        disabledScopes={pointInTime.disabledScopes}
        stats={pointInTime.stats}
        metricsStatus={pointInTime.metricsStatus}
        disabledSizeSegments={pointInTime.disabledSizeSegments}
        disabledCyclicality={pointInTime.disabledCyclicality}
        onUniverseSelect={pointInTime.handleUniverseSelect}
        onGeographicToggle={pointInTime.handleGeographicToggle}
        onSizeToggle={pointInTime.handleSizeToggle}
        onCycleToggle={pointInTime.handleCyclicalityToggle}
        scopeExcludedCount={filterExcludedCounts.scopeExcluded}
        sizeExcludedCount={filterExcludedCounts.sizeSegmentExcluded}
        cyclicalityExcludedCount={filterExcludedCounts.cyclicalityExcluded}
      />

      <EligibilityRules
        securityType={eligibility.securityType}
        liquidity={eligibility.liquidity}
      />

      {/* Hairline separator before the Benchmark section (Figma). */}
      <div className="border-t border-border/60" />

      <BenchmarkSelection benchmark={benchmark} />
    </StepCard>
  );
}
